import { type TypedDocumentNode, gql } from '@apollo/client';
import { FormattedNumber } from 'react-intl';
import styled, { type CSSProperties } from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Percentage } from '@sorare/core/src/atoms/icons/Percentage';
import { Horizontal, SBHorizontal } from '@sorare/core/src/atoms/layout/flex';
import { BrandXXS } from '@sorare/core/src/atoms/typography';
import { isType } from '@sorare/core/src/lib/gql';
import { findThresholdColor } from '@sorare/core/src/lib/thresholds';
import type { Color } from '@sorare/core/src/style/types';

import type {
  GameWinOddsProjection_teamGameStatsInterface,
  GameWinOddsProjection_teamGameStatsInterface_FootballTeamGameStats_,
} from './__generated__/index.graphql';

const Root = styled(SBHorizontal)`
  position: relative;
  height: 100%;
  width: 100%;
  padding: 0 var(--unit);

  background: linear-gradient(
    to right,
    var(--c-left-color, transparent) -100%,
    transparent,
    var(--c-right-color, transparent) 200%
  );
`;
const OddRoot = styled(Horizontal).attrs({ gap: 0 })`
  gap: 2px;
`;

export const validStat = (
  stat?: GameWinOddsProjection_teamGameStatsInterface | null
): stat is NonNullable<GameWinOddsProjection_teamGameStatsInterface_FootballTeamGameStats_> =>
  isType(stat, 'FootballTeamGameStats') && !!stat.winOddsBasisPoints;

type OddProps = {
  odd: number;
  color?: Color;
};
const Odd = ({ odd, color = 'var(--c-nd-600)' }: OddProps) => (
  <OddRoot>
    <BrandXXS bold color={color}>
      <FormattedNumber value={odd} maximumFractionDigits={0} />
    </BrandXXS>
    <Percentage color={color} />
  </OddRoot>
);

type Props = {
  isPlayerFromHomeTeam?: boolean;
  homeTeamGameStatsInterface?: GameWinOddsProjection_teamGameStatsInterface | null;
  awayTeamGameStatsInterface?: GameWinOddsProjection_teamGameStatsInterface | null;
};
export const GameWinOddsProjection = ({
  isPlayerFromHomeTeam,
  homeTeamGameStatsInterface,
  awayTeamGameStatsInterface,
}: Props) => {
  if (
    !(
      validStat(homeTeamGameStatsInterface) &&
      homeTeamGameStatsInterface?.winOddsBasisPoints &&
      validStat(awayTeamGameStatsInterface) &&
      awayTeamGameStatsInterface?.winOddsBasisPoints
    )
  ) {
    return null;
  }
  const percentageHomeTeamWinOdds =
    homeTeamGameStatsInterface?.winOddsBasisPoints / 100;
  const percentageAwayTeamWinOdds =
    awayTeamGameStatsInterface?.winOddsBasisPoints / 100;

  const percentageDrawOdds =
    100 - percentageHomeTeamWinOdds - percentageAwayTeamWinOdds;

  const homeTeamTextColor = findThresholdColor(
    percentageHomeTeamWinOdds,
    Sport.FOOTBALL
  ).color;
  const awayTeamTextColor = findThresholdColor(
    percentageAwayTeamWinOdds,
    Sport.FOOTBALL
  ).color;

  const bothSideColors = isPlayerFromHomeTeam === undefined;
  const leftColor = isPlayerFromHomeTeam ? homeTeamTextColor : undefined;
  const rightColor =
    isPlayerFromHomeTeam === false ? awayTeamTextColor : undefined;

  return (
    <Root
      style={
        {
          '--c-left-color': bothSideColors ? homeTeamTextColor : leftColor,
          '--c-right-color': bothSideColors ? awayTeamTextColor : rightColor,
        } as CSSProperties
      }
    >
      <Odd color={homeTeamTextColor} odd={percentageHomeTeamWinOdds} />
      <Odd color="var(--c-nd-600)" odd={percentageDrawOdds} />
      <Odd color={awayTeamTextColor} odd={percentageAwayTeamWinOdds} />
    </Root>
  );
};

GameWinOddsProjection.fragments = {
  teamGameStatsInterface: gql`
    fragment GameWinOddsProjection_teamGameStatsInterface on TeamGameStatsInterface {
      id
      ... on FootballTeamGameStats {
        id
        winOddsBasisPoints
      }
    }
  ` as TypedDocumentNode<GameWinOddsProjection_teamGameStatsInterface>,
};
